import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Chartjs provides bundle-reducing options:
// https://www.chartjs.org/docs/latest/getting-started/integration.html#quick-start
import 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import { Button, ButtonGroup, Col, Row, Spinner, Table } from 'reactstrap';
import { appendPath } from '@truqc/content-path';
import * as ajax from '../../../../actions/ajax';
import notificationActions from '../../../../actions/notificationActionCreators';
import logger from '../../../../utils/logger';
import entityComposition from '../../../common/classes/entityComposition';
import onChangeFunc from '../../commonActions/onChange';
import { contentSelector } from '../../selectors/index';
import MetricCard from '../qualityAgentView/metricCard';
const DAY_OPTIONS = [7, 28];
const normalizeDashboardDays = (raw, fallback = 7) => {
  const parsed = Number(raw);
  if (DAY_OPTIONS.includes(parsed)) {
    return parsed;
  }
  const fallbackParsed = Number(fallback);
  if (DAY_OPTIONS.includes(fallbackParsed)) {
    return fallbackParsed;
  }
  return 7;
};
const TREND_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0
      }
    }
  }
};
const formatCustomers = customers => {
  if (!Array.isArray(customers) || customers.length === 0) {
    return '—';
  }
  if (customers.length <= 3) {
    return customers.join(', ');
  }
  return `${customers.slice(0, 3).join(', ')} +${customers.length - 3}`;
};
const formatSinceLabel = since => {
  if (!since) {
    return '';
  }
  const parsed = new Date(since);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return ` · since ${parsed.toLocaleString()}`;
};
const formatRollupAsOfSubtitle = (rollup, emptyHints) => {
  if (!rollup || rollup.available === false) {
    if ((rollup === null || rollup === void 0 ? void 0 : rollup.reason) === 'rollups-unavailable') {
      return emptyHints.unavailable;
    }
    return emptyHints.noForm;
  }
  if (!rollup.pulledAt) {
    return emptyHints.noPull;
  }
  const asOf = formatSinceLabel(rollup.pulledAt).replace(' · since ', 'as of ');
  // Prefer surfacing collector reason (e.g. local CloudWatch skip) even when
  // stale — otherwise local envs only show "stale" and hide the useful hint.
  const parts = [];
  if (asOf) {
    parts.push(asOf);
  }
  if (rollup.stale) {
    parts.push('stale');
  }
  if (rollup.reason) {
    parts.push(rollup.reason);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Form-backed rollup';
};
const formatSyncBlockedSubtitle = syncBlocked => formatRollupAsOfSubtitle(syncBlocked, {
  noForm: 'No collector form yet — create one in Health Collectors',
  noPull: 'Collector form exists — run collect to populate',
  unavailable: 'Rollups unavailable'
});
const formatAvgSyncSubtitle = avgSync => {
  const base = formatRollupAsOfSubtitle(avgSync, {
    noForm: 'No collector form yet — create one in Health Collectors',
    noPull: 'Collector form exists — run collect to populate',
    unavailable: 'Rollups unavailable'
  });
  if (!(avgSync !== null && avgSync !== void 0 && avgSync.available) || avgSync.pulledAt == null) {
    return base;
  }
  const parts = [base];
  if (avgSync.syncSuccessPercent != null && Number.isFinite(avgSync.syncSuccessPercent)) {
    parts.push(`${avgSync.syncSuccessPercent.toFixed(0)}% ok`);
  }
  if (avgSync.avgInitialSeconds != null && Number.isFinite(avgSync.avgInitialSeconds)) {
    parts.push(`init ${avgSync.avgInitialSeconds.toFixed(0)}s`);
  }
  if (avgSync.avgIncrementalSeconds != null && Number.isFinite(avgSync.avgIncrementalSeconds)) {
    parts.push(`incr ${avgSync.avgIncrementalSeconds.toFixed(0)}s`);
  }
  return parts.filter(Boolean).join(' · ');
};
const formatRdsSubtitle = rds => {
  const base = formatRollupAsOfSubtitle(rds, {
    noForm: 'No collector form yet — create one in Health Collectors',
    noPull: 'Collector form exists — run collect to populate',
    unavailable: 'Rollups unavailable'
  });
  if (!(rds !== null && rds !== void 0 && rds.available) || rds.pulledAt == null) {
    return base;
  }
  const parts = [base];
  if (rds.rdsConnections != null && Number.isFinite(rds.rdsConnections)) {
    parts.push(`${Math.round(rds.rdsConnections)} conn`);
  }
  if (rds.replicaLagSeconds != null && Number.isFinite(rds.replicaLagSeconds)) {
    parts.push(`${rds.replicaLagSeconds.toFixed(1)}s lag`);
  }
  return parts.filter(Boolean).join(' · ');
};

/**
 * Format seconds for MetricCard (e.g. 12.3s). Null when unavailable.
 * @param {number|null|undefined} seconds
 * @returns {string|null}
 */
const formatAvgSyncValue = seconds => {
  if (seconds == null || !Number.isFinite(seconds)) {
    return null;
  }
  return `${seconds.toFixed(1)}s`;
};

/**
 * Format RDS CPU % for MetricCard (e.g. 42%). Null when unavailable.
 * @param {number|null|undefined} percent
 * @returns {string|null}
 */
const formatRdsCpuValue = percent => {
  if (percent == null || !Number.isFinite(percent)) {
    return null;
  }
  return `${percent.toFixed(0)}%`;
};

/**
 * Card / handoff title for WAF rollup — mode-aware so beta Count is not "blocked".
 * @param {{ mode?: string }|null|undefined} waf
 * @returns {string}
 */
const formatWafTitle = waf => {
  if ((waf === null || waf === void 0 ? void 0 : waf.mode) === 'count') {
    return 'WAF counted';
  }
  if ((waf === null || waf === void 0 ? void 0 : waf.mode) === 'block') {
    return 'WAF blocked';
  }
  return 'WAF requests';
};
const formatWafSubtitle = waf => {
  const base = formatRollupAsOfSubtitle(waf, {
    noForm: 'No collector form yet — create one in Health Collectors',
    noPull: 'Collector form exists — run collect to populate',
    unavailable: 'Rollups unavailable'
  });
  if (!(waf !== null && waf !== void 0 && waf.available) || waf.pulledAt == null) {
    return base;
  }
  const parts = [base];
  if (waf.metricName) {
    parts.push(waf.metricName);
  }
  return parts.filter(Boolean).join(' · ');
};
const formatScalingSubtitle = scaling => {
  const base = formatRollupAsOfSubtitle(scaling, {
    noForm: 'No collector form yet — create one in Health Collectors',
    noPull: 'Collector form exists — run collect to populate',
    unavailable: 'Rollups unavailable'
  });
  if (!(scaling !== null && scaling !== void 0 && scaling.available) || scaling.pulledAt == null) {
    return base;
  }
  const parts = [base];
  if (scaling.serverCpuPercent != null && Number.isFinite(scaling.serverCpuPercent)) {
    parts.push(`${scaling.serverCpuPercent.toFixed(0)}% CPU`);
  }
  if (scaling.serviceName) {
    parts.push(scaling.serviceName);
  }
  return parts.filter(Boolean).join(' · ');
};

/**
 * Format WAF request count for MetricCard.
 * @param {number|null|undefined} count
 * @returns {string|null}
 */
const formatWafValue = count => {
  if (count == null || !Number.isFinite(count)) {
    return null;
  }
  return String(Math.round(count));
};

/**
 * Format running task count for MetricCard.
 * @param {number|null|undefined} count
 * @returns {string|null}
 */
const formatScalingValue = count => {
  if (count == null || !Number.isFinite(count)) {
    return null;
  }
  return String(Number(count.toFixed(1)));
};

/**
 * Health scorecard (Phase 1 live errors + Phase 2 Form-backed rollups).
 * Live error metrics via GET /health/errors; rollups via GET /health/rollups.
 */
const HealthDashboardView = ({
  label: _label = 'Health',
  days: initialDays = 7,
  content: _content = null,
  onChange: _onChange = undefined,
  parentContentPath: _parentContentPath = '/',
  slackHandoffDaysContentPath: _slackHandoffDaysContentPath = 'slackHandoffDays',
  type: _type = 'healthDashboardView'
}) => {
  const [days, setDays] = useState(() => normalizeDashboardDays(_content === null || _content === void 0 ? void 0 : _content[_slackHandoffDaysContentPath], initialDays));
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [syncBlocked, setSyncBlocked] = useState(null);
  const [avgSync, setAvgSync] = useState(null);
  const [rds, setRds] = useState(null);
  const [waf, setWaf] = useState(null);
  const [scaling, setScaling] = useState(null);
  const requestIdRef = useRef(0);
  const persistHandoffDays = useCallback(async windowDays => {
    if (!_onChange || !_slackHandoffDaysContentPath) {
      return;
    }
    const handoffPath = appendPath(_parentContentPath, _slackHandoffDaysContentPath);
    await _onChange({
      path: handoffPath,
      value: windowDays,
      changeModifiers: {
        localChange: true,
        parentContentPath: _parentContentPath,
        type: _type
      }
    });
  }, [_onChange, _parentContentPath, _slackHandoffDaysContentPath, _type]);
  const handleDaysSelect = useCallback(async option => {
    if (option === days) {
      return;
    }
    try {
      await persistHandoffDays(option);
      setDays(option);
    } catch (err) {
      logger('Failed to persist slack handoff days', err === null || err === void 0 ? void 0 : err.message);
      notificationActions.info({
        message: 'Failed to save the reporting window. Please try again.'
      }, {
        autoClose: 5000
      });
    }
  }, [days, persistHandoffDays]);
  useEffect(() => {
    const fromContent = _content === null || _content === void 0 ? void 0 : _content[_slackHandoffDaysContentPath];
    if (fromContent == null || fromContent === '') {
      return;
    }
    const normalized = normalizeDashboardDays(fromContent, initialDays);
    setDays(current => current === normalized ? current : normalized);
  }, [_content, initialDays, _slackHandoffDaysContentPath]);
  const loadMetrics = useCallback(async windowDays => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setErrorMessage(null);
    try {
      var _rollupsResponse$data, _rollupsResponse$data2, _rollupsResponse$data3, _rollupsResponse$data4, _rollupsResponse$data5;
      const [errorsResponse, rollupsResponse] = await Promise.all([ajax.get('/health/errors', {
        days: windowDays
      }), ajax.get('/health/rollups', {
        keys: 'syncBlocked,avgSync,rds,waf,scaling'
      }).catch(err => {
        logger('Failed to load health rollups', err === null || err === void 0 ? void 0 : err.message);
        const rollupsUnavailable = {
          available: false,
          reason: 'rollups-unavailable'
        };
        return {
          data: {
            syncBlocked: rollupsUnavailable,
            avgSync: rollupsUnavailable,
            rds: rollupsUnavailable,
            waf: rollupsUnavailable,
            scaling: rollupsUnavailable
          }
        };
      })]);
      if (requestId !== requestIdRef.current) {
        return;
      }
      setMetrics(errorsResponse.data);
      setSyncBlocked((rollupsResponse === null || rollupsResponse === void 0 || (_rollupsResponse$data = rollupsResponse.data) === null || _rollupsResponse$data === void 0 ? void 0 : _rollupsResponse$data.syncBlocked) || null);
      setAvgSync((rollupsResponse === null || rollupsResponse === void 0 || (_rollupsResponse$data2 = rollupsResponse.data) === null || _rollupsResponse$data2 === void 0 ? void 0 : _rollupsResponse$data2.avgSync) || null);
      setRds((rollupsResponse === null || rollupsResponse === void 0 || (_rollupsResponse$data3 = rollupsResponse.data) === null || _rollupsResponse$data3 === void 0 ? void 0 : _rollupsResponse$data3.rds) || null);
      setWaf((rollupsResponse === null || rollupsResponse === void 0 || (_rollupsResponse$data4 = rollupsResponse.data) === null || _rollupsResponse$data4 === void 0 ? void 0 : _rollupsResponse$data4.waf) || null);
      setScaling((rollupsResponse === null || rollupsResponse === void 0 || (_rollupsResponse$data5 = rollupsResponse.data) === null || _rollupsResponse$data5 === void 0 ? void 0 : _rollupsResponse$data5.scaling) || null);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      logger('Failed to load health error metrics', err === null || err === void 0 ? void 0 : err.message);
      setMetrics(null);
      setSyncBlocked(null);
      setAvgSync(null);
      setRds(null);
      setWaf(null);
      setScaling(null);
      const envelope = ajax.getError(err);
      const debug = (envelope.error_debug || '').replace(/^DEBUG:\s*/i, '').trim();
      setErrorMessage(envelope.error_detail || debug || envelope.error_msg || 'Failed to load error metrics.');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);
  useEffect(() => {
    loadMetrics(days);
  }, [days, loadMetrics]);
  const trendChart = useMemo(() => {
    const trend = Array.isArray(metrics === null || metrics === void 0 ? void 0 : metrics.trend) ? metrics.trend : [];
    return {
      labels: trend.map(point => point.day),
      datasets: [{
        label: 'Errors',
        data: trend.map(point => point.count),
        borderColor: '#f0ad4e',
        backgroundColor: 'rgba(240, 173, 78, 0.2)',
        tension: 0.2,
        fill: true
      }]
    };
  }, [metrics]);
  const hasTrendPoints = ((metrics === null || metrics === void 0 ? void 0 : metrics.trend) || []).length > 0;
  const syncBlockedValue = syncBlocked !== null && syncBlocked !== void 0 && syncBlocked.available && syncBlocked.pulledAt != null ? syncBlocked.blockedUserCount : null;
  const avgSyncValue = avgSync !== null && avgSync !== void 0 && avgSync.available && avgSync.pulledAt != null ? formatAvgSyncValue(avgSync.avgSyncSeconds) : null;
  const rdsValue = rds !== null && rds !== void 0 && rds.available && rds.pulledAt != null ? formatRdsCpuValue(rds.rdsCpuPercent) : null;
  const wafValue = waf !== null && waf !== void 0 && waf.available && waf.pulledAt != null ? formatWafValue(waf.requestCount) : null;
  const scalingValue = scaling !== null && scaling !== void 0 && scaling.available && scaling.pulledAt != null ? formatScalingValue(scaling.runningTaskCount) : null;
  return /*#__PURE__*/React.createElement("div", {
    className: "t-health-dashboard p-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-wrap align-items-center justify-content-between mb-3",
    style: {
      gap: '0.5rem'
    }
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", {
    className: "mb-1"
  }, _label), /*#__PURE__*/React.createElement("div", {
    className: "text-muted small"
  }, "Live errors + Form-backed rollups", formatSinceLabel(metrics === null || metrics === void 0 ? void 0 : metrics.since))), /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-wrap align-items-center",
    style: {
      gap: '0.5rem'
    }
  }, /*#__PURE__*/React.createElement(ButtonGroup, {
    size: "sm"
  }, DAY_OPTIONS.map(option => /*#__PURE__*/React.createElement(Button, {
    key: option,
    color: days === option ? 'primary' : 'secondary',
    outline: days !== option,
    onClick: () => handleDaysSelect(option)
  }, `${option}d`))))), errorMessage && /*#__PURE__*/React.createElement("div", {
    className: "alert alert-danger",
    role: "alert"
  }, errorMessage, /*#__PURE__*/React.createElement(Button, {
    color: "link",
    className: "p-0 ms-2 align-baseline",
    onClick: () => loadMetrics(days)
  }, "Retry")), loading && !metrics ? /*#__PURE__*/React.createElement("div", {
    className: "text-center py-5"
  }, /*#__PURE__*/React.createElement(Spinner, {
    color: "primary",
    "data-testid": "t-health-spinner"
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Row, {
    className: "g-3 mb-4"
  }, /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: `Errors (${days}d)`,
    value: loading ? '…' : metrics === null || metrics === void 0 ? void 0 : metrics.errorCount,
    subtitle: "Live from error cabinet",
    color: "warning"
  })), /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: "Unique types",
    value: loading ? '…' : metrics === null || metrics === void 0 ? void 0 : metrics.uniqueTypes,
    subtitle: "Distinct error titles"
  })), /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: "Sync-blocked users",
    value: loading ? '…' : syncBlockedValue,
    subtitle: formatSyncBlockedSubtitle(syncBlocked)
  })), /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: "iOS sync",
    value: loading ? '…' : avgSyncValue,
    subtitle: formatAvgSyncSubtitle(avgSync)
  }))), /*#__PURE__*/React.createElement(Row, {
    className: "g-3 mb-4"
  }, /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: "RDS",
    value: loading ? '…' : rdsValue,
    subtitle: formatRdsSubtitle(rds)
  })), /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: formatWafTitle(waf),
    value: loading ? '…' : wafValue,
    subtitle: formatWafSubtitle(waf)
  })), /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: "Scaling",
    value: loading ? '…' : scalingValue,
    subtitle: formatScalingSubtitle(scaling)
  }))), /*#__PURE__*/React.createElement("div", {
    className: "mb-2 fw-semibold"
  }, `Error trend (${days}d)`), /*#__PURE__*/React.createElement("div", {
    className: "bg-white border rounded p-3 mb-4",
    style: {
      height: 220
    },
    "data-testid": "t-health-trend"
  }, hasTrendPoints ? /*#__PURE__*/React.createElement(Line, {
    data: trendChart,
    options: TREND_OPTIONS
  }) : /*#__PURE__*/React.createElement("div", {
    className: "text-muted small h-100 d-flex align-items-center justify-content-center"
  }, loading ? 'Loading…' : 'No trend data for this window.')), /*#__PURE__*/React.createElement("div", {
    className: "mb-2 fw-semibold"
  }, "Top error types"), /*#__PURE__*/React.createElement(Table, {
    responsive: true,
    striped: true,
    size: "sm",
    className: "bg-white"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Title"), /*#__PURE__*/React.createElement("th", {
    className: "text-end"
  }, "Count"), /*#__PURE__*/React.createElement("th", null, "Customers"))), /*#__PURE__*/React.createElement("tbody", null, ((metrics === null || metrics === void 0 ? void 0 : metrics.topTypes) || []).length === 0 ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    colSpan: 3,
    className: "text-muted"
  }, loading ? 'Loading…' : 'No errors in this window.')) : metrics.topTypes.map((row, index) =>
  /*#__PURE__*/
  // Titles can duplicate (e.g. multiple '(untitled)'); index disambiguates.
  // eslint-disable-next-line react/no-array-index-key
  React.createElement("tr", {
    key: `${row.title}-${index}`
  }, /*#__PURE__*/React.createElement("td", null, row.title), /*#__PURE__*/React.createElement("td", {
    className: "text-end"
  }, row.count), /*#__PURE__*/React.createElement("td", null, formatCustomers(row.customers)))))), /*#__PURE__*/React.createElement("p", {
    className: "text-muted small mb-0"
  }, "Collector cards come from singleton Forms in Health Collectors (scheduled execute). iOS sync is fleet-wide CloudWatch SyncDuration (duration primary; success % and init/incr in subtitle). RDS / WAF / Scaling are env-scoped CloudWatch rollups. This view does not fleet-scan or call CloudWatch on load.")));
};
HealthDashboardView.propTypes = {
  label: PropTypes.string,
  days: PropTypes.number,
  content: PropTypes.shape({}),
  onChange: PropTypes.func,
  parentContentPath: PropTypes.string,
  slackHandoffDaysContentPath: PropTypes.string,
  type: PropTypes.string
};
export { HealthDashboardView as UncomposedHealthDashboardView };
export default entityComposition(HealthDashboardView, [], {
  content: contentSelector,
  onChange: onChangeFunc
});